/**
 * Step 1: basic wedding details — couple names + roles, wedding name, date,
 * budget, currency. Captures partner identities up-front so the dashboard
 * and create-wedding flow can lead with "<Partner 1> & <Partner 2>".
 *
 * Submits via `<form id="wedding-form">` so the stepper footer's Next button
 * can trigger native HTML form validation.
 */
import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import DateInput from '../ui/date-input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const CURRENCIES = ['USD', 'INR', 'GBP', 'CAD', 'EUR', 'AUD', 'PKR', 'BDT', 'LKR', 'NPR'];
const ROLES = [
  { value: 'bride',   label: 'Bride' },
  { value: 'groom',   label: 'Groom' },
  { value: 'partner', label: 'Partner' },
];

function PartnerRow({ idx, formData, setFormData }) {
  const nameKey = `partner${idx}_name`;
  const roleKey = `partner${idx}_role`;
  return (
    <div className="grid grid-cols-[1fr_140px] gap-3">
      <div className="space-y-2">
        <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">
          Partner {idx} {idx === 1 ? '(yours)' : ''}
        </Label>
        <Input
          value={formData[nameKey] || ''}
          onChange={(e) => setFormData({ ...formData, [nameKey]: e.target.value })}
          className="input-field h-12"
          placeholder={idx === 1 ? 'e.g., Riya' : 'e.g., Arjun'}
          data-testid={`wedding-partner${idx}-name-input`}
        />
      </div>
      <div className="space-y-2">
        <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Role</Label>
        <Select
          value={formData[roleKey] || ''}
          onValueChange={(value) => setFormData({ ...formData, [roleKey]: value })}
        >
          <SelectTrigger className="input-field h-12" data-testid={`wedding-partner${idx}-role-select`}>
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            {ROLES.map((r) => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export default function WeddingDetailsStep({ formData, setFormData, onNext }) {
  return (
    <form onSubmit={onNext} id="wedding-form" className="space-y-5 mt-4">
      <PartnerRow idx={1} formData={formData} setFormData={setFormData} />
      <PartnerRow idx={2} formData={formData} setFormData={setFormData} />
      <div className="space-y-2">
        <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Wedding Name</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="input-field h-12"
          placeholder="e.g., Sarah & Raj's Wedding"
          required
          data-testid="wedding-name-input"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Wedding Date</Label>
        <DateInput
          value={formData.wedding_date}
          onChange={(iso) => setFormData({ ...formData, wedding_date: iso })}
          className="input-field h-12 font-mono"
          data-testid="wedding-date-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Total Budget</Label>
          <Input
            type="number"
            value={formData.total_budget}
            onChange={(e) => setFormData({ ...formData, total_budget: e.target.value })}
            className="input-field h-12 font-mono"
            placeholder="0"
            min="0"
            step="0.01"
            data-testid="wedding-budget-input"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Currency</Label>
          <Select
            value={formData.preferred_currency}
            onValueChange={(value) => setFormData({ ...formData, preferred_currency: value })}
          >
            <SelectTrigger className="input-field h-12" data-testid="wedding-currency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((currency) => (
                <SelectItem key={currency} value={currency}>{currency}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </form>
  );
}
